import {
  Tooltip,
  Text,
  Button,
  MantineSize,
  type ButtonProps
} from '@mantine/core'
import { IconLoader2, IconQuestionMark } from '@tabler/icons-react'
import { type MouseEvent, ReactElement } from 'react'
import { cn } from '../../utils'

type Variants =
  | 'default'
  | 'filled'
  | 'primary'
  | 'outline'
  | 'subtle'
  | 'alert'
  | 'transparent'
  | 'paper'

type Size = MantineSize

/** Where the control sits: shell (blue chrome) vs surface (cards/modals). */
export type CircleButtonTone = 'on-shell' | 'on-surface'

export type CircleButtonProps = {
  onClick?: (event: MouseEvent<HTMLButtonElement>) => void
  icon?: ReactElement
  tooltip?: string
  tooltipDelay?: number
  label?: string
  variant?: Variants
  size?: MantineSize
  className?: string
  loading?: boolean
  disabled?: boolean
  type?: 'button' | 'submit'
  testid?: string
  /** Mantine palette key (e.g. `red`, `brand`) — forwarded to Mantine `Button` */
  color?: ButtonProps['color']
  children?: ReactElement | string | number
  /**
   * Extra icon contrast on shell chrome when variant is transparent/outline
   * (StepCare theme does not set fg on transparent root).
   */
  tone?: CircleButtonTone
}

const baseStyles =
  'group h-auto cursor-pointer items-center gap-0 rounded-3xl px-2 transition-all duration-200 border-none'

/**
 * Map to Mantine `variant` so `mantineTheme` → `stepcareButtonRootClassNames`
 * applies the same root classes as `@packages/ui` Button.
 */
function toMantineVariant(v: Variants): ButtonProps['variant'] {
  switch (v) {
    case 'paper':
      return 'light'
    case 'alert':
      return 'danger' as ButtonProps['variant']
    case 'primary':
      return 'primary' as ButtonProps['variant']
    case 'default':
    case 'filled':
      return 'filled'
    default:
      return v as ButtonProps['variant']
  }
}

function toneClass(variant: Variants, tone: CircleButtonTone): string {
  if (tone !== 'on-shell') return ''
  if (variant === 'transparent' || variant === 'outline') {
    return 'text-shell-foreground'
  }
  return ''
}

const sizes: Record<Size, string> = {
  xs: 'p-1 px-2',
  sm: 'p-2 px-2.5',
  md: 'p-3 px-3.5',
  lg: 'p-4',
  xl: 'p-5'
}

const iconHoverScale: Record<Size, string> = {
  xs: 'group-hover:scale-105 ',
  sm: 'group-hover:scale-105',
  md: 'group-hover:scale-110',
  lg: 'group-hover:scale-115',
  xl: 'group-hover:scale-120'
}

export const CircleButton = ({
  onClick = () => null,
  icon = <IconQuestionMark />,
  label,
  tooltip,
  variant = 'default',
  size = 'sm',
  className = '',
  loading = false,
  disabled = false,
  type = 'button',
  tooltipDelay = 1000,
  children,
  color,
  testid = 'circle-button',
  tone = 'on-surface'
}: CircleButtonProps) => {
  const mantineVariant = toMantineVariant(variant)
  const mantineColor: ButtonProps['color'] | undefined =
    variant === 'alert'
      ? undefined
      : color !== undefined && color !== ''
        ? color
        : undefined

  return (
    <Tooltip label={tooltip} disabled={!tooltip} openDelay={tooltipDelay}>
      <Button
        data-testid={testid}
        role={'button'}
        type={type}
        variant={mantineVariant}
        color={mantineColor}
        onClick={(e: MouseEvent<HTMLButtonElement>) =>
          !disabled && !loading && onClick?.(e)
        }
        leftSection={loading ? <IconLoader2 className="animate-spin" /> : icon}
        classNames={{
          root: children || label ? '' : 'aspect-square',
          section: 'm-0',
          label: label ? 'mx-2' : undefined
        }}
        className={cn(
          baseStyles,
          toneClass(variant, tone),
          sizes[size],
          iconHoverScale[size],
          disabled && 'opacity-50',
          className
        )}
      >
        {children || (label && <Text size={size}>{label}</Text>)}
      </Button>
    </Tooltip>
  )
}
